import React, { useState, ReactNode, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { TabItem } from './ETabs';

interface SidebarTabsProps {
  tabs: TabItem[];
  initialActiveIndex?: number;
  onTabChange?: (index: number) => void;
}

export const SidebarTabs: React.FC<SidebarTabsProps> = ({
  tabs,
  initialActiveIndex = 0,
  onTabChange,
}) => {
  const [activeIndex, setActiveIndex] = useState(initialActiveIndex);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const handleTabClick = (index: number) => {
    if (index === activeIndex) return; // Gereksiz re-render önle
    setActiveIndex(index);
    if (onTabChange) {
      onTabChange(index);
    }
  };

  const checkForScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }
  };

  useEffect(() => {
    checkForScroll();
    window.addEventListener('resize', checkForScroll);
    return () => window.removeEventListener('resize', checkForScroll);
  }, [tabs]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const { clientWidth } = scrollContainerRef.current;
      const scrollAmount = clientWidth * 0.6; // Ekranın %60'ı kadar kaydır
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="w-full flex flex-col md:flex-row gap-0 md:gap-6   relative card2 p-0">

      {/* 📱 MOBILE: Horizontal Scrollable Tabs (Matches ETabs) */}
      <div className="md:hidden card p-0 border-borderColor w-full bg-card relative mb-4">
        <div className="relative w-full border-b border-borderColor bg-bgAlt rounded-t-lg group">
          {canScrollLeft && (
            <div className="absolute left-0 top-0 bottom-0 z-30 flex items-center bg-gradient-to-r from-bgAlt via-bgAlt to-transparent w-16 pl-2">
              <button onClick={() => scroll('left')} className="p-1.5 rounded-full bg-card shadow-md border border-borderColor text-text opacity-80 hover:opacity-100 hover:text-primary hover:bg-bgAlt transition-all">
                <ChevronLeft size={16} />
              </button>
            </div>
          )}

          <div
            ref={scrollContainerRef}
            onScroll={checkForScroll}
            className="flex overflow-x-auto hide-scrollbar gap-2 px-3 pt-2 relative z-10 scroll-smooth"
          >
            {tabs.map((tab, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(index)}
                  className={`
                    relative flex items-center justify-center gap-2 px-5 py-3.5 text-[14px] font-bold transition-colors duration-200 whitespace-nowrap outline-none
                    ${isActive
                      ? 'text-primary'
                      : 'text-text opacity-70 hover:opacity-100 hover:text-primary hover:bg-bgAlt rounded-t-xl'
                    }
                  `}
                  role="tab"
                  aria-selected={isActive}
                >
                  <span className="relative z-10 flex items-center gap-2">
                    {tab.header}
                  </span>
                  {isActive && (
                    <motion.div
                      layoutId="mobileActiveTabIndicator"
                      className="absolute bottom-[-1px] left-0 right-0 h-[3px] bg-primary rounded-t-full z-20 shadow-sm"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 35 }}
                    />
                  )}
                  {isActive && (
                    <motion.div
                      layoutId="mobileActiveTabBg"
                      className="absolute inset-0 bg-bgAlt rounded-t-xl z-0"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 35 }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {canScrollRight && (
            <div className="absolute right-0 top-0 bottom-0 z-30 flex items-center justify-end bg-gradient-to-l from-bgAlt via-bgAlt to-transparent w-16 pr-2">
              <button onClick={() => scroll('right')} className="p-1.5 rounded-full bg-card shadow-md border border-borderColor text-text opacity-80 hover:opacity-100 hover:text-primary hover:bg-bgAlt transition-all">
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 💻 DESKTOP: Vertical Sidebar */}
      <div className="hidden md:flex flex-col w-[260px] shrink-0 sticky top-4 h-fit gap-2  p-4">
        <h3 className="text-xs font-black uppercase opacity-50 px-4 mb-2 tracking-wider">Menü</h3>
        <div className="flex flex-col gap-1">
          {tabs.map((tab, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(index)}
                className={`
                  relative flex items-center gap-3 px-4 py-3.5 text-sm font-bold rounded-xl transition-all duration-300 w-full text-left outline-none overflow-hidden group
                  ${isActive
                    ? 'text-primary bg-primary/10 shadow-sm'
                    : 'text-text opacity-70 hover:opacity-100 hover:bg-bgAlt hover:text-primary hover:shadow-sm'
                  }
                `}
                role="tab"
                aria-selected={isActive}
              >
                {isActive && (
                  <motion.div
                    layoutId="desktopActiveTabIndicator"
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-3/4 bg-primary rounded-r-full shadow-[0_0_8px_rgba(var(--primary-color),0.5)]"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                  />
                )}
                <span className={`relative z-10 flex items-center gap-2 transform transition-transform duration-300 ${isActive ? 'translate-x-1' : 'group-hover:translate-x-1'}`}>
                  {tab.header}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 📄 CONTENT AREA */}
      <div className="flex-1 w-full min-w-0 card p-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, y: 15, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -15, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="w-full"
          >
            {tabs[activeIndex] && tabs[activeIndex].content}
          </motion.div>
        </AnimatePresence>
      </div>

    </div>
  );
};
